import { useSelector } from 'react-redux';
import { HelpTooltip } from 'ui/tooltip';
import { colors } from 'ui/theme';
import { formatAsPercent, localize } from 'utils/numberHelpers';
import { selectIsIterableGoalsStretchFlagOn } from 'core/userContext';

import {
  HelpIconProps,
  GoalAnalyticsMetricKey,
  PeriodReportingProps,
} from 'modules/iterableGoals/types';
import { useGoalsTranslations } from 'modules/iterableGoals/hooks';
import { localizeCurrencyValue } from 'utils/i18nHelpers';
import { getPercentChangeInfo } from './utils';

import {
  PerformanceMetricWrapper,
  MetricTitle,
  MetricValue,
  HelpIconWrapper,
  PeriodReportingWrapper,
  EmptyPeriodReportingWrapper,
  PeriodText,
  PeriodPercentWrapper,
  PeriodIconWrapper,
} from './Styled';

const HelpIcon = ({ tooltipText }: HelpIconProps) => {
  if (!tooltipText) return null;
  return (
    <HelpIconWrapper>
      <HelpTooltip tooltipContent={tooltipText} iconProps={{ color: colors.secondaryText }} />
    </HelpIconWrapper>
  );
};

const PeriodReporting = ({ value, previousValue }: PeriodReportingProps) => {
  const { t } = useGoalsTranslations();
  const { text, emptyStateText } = t<string, Record<string, string>>(
    'goalPerformance.periodReporting',
    { returnObjects: true },
  );
  const notEnoughData = !previousValue || !value;

  if (notEnoughData)
    return (
      <EmptyPeriodReportingWrapper data-test="empty-period-reporting-wrapper">
        {emptyStateText}
      </EmptyPeriodReportingWrapper>
    );

  // Determines which icon and color to use for displaying percentage delta
  const { percentChange, iconBackgroundColor, iconColor, icon } = getPercentChangeInfo(
    value,
    previousValue,
  );

  return (
    <PeriodReportingWrapper>
      <PeriodIconWrapper backgroundColor={iconBackgroundColor}>{icon}</PeriodIconWrapper>
      <PeriodPercentWrapper color={iconColor}>
        {formatAsPercent(percentChange)}
      </PeriodPercentWrapper>
      <PeriodText>{text}</PeriodText>
    </PeriodReportingWrapper>
  );
};

type Props = {
  valueKey: GoalAnalyticsMetricKey;
  value: number;
  previousValue: number;
  infoText: string;
  title: string;
  isCurrency?: boolean;
};

const percentageDisplayMetrics = [
  GoalAnalyticsMetricKey.TOTAL_GOAL_CONVERSIONS_BY_TOTAL_MESSAGES_SENT,
  GoalAnalyticsMetricKey.UNIQUE_GOAL_CONVERSIONS_BY_UNIQUE_MESSAGES_SENT,
];

export const PerformanceReportingCard = ({
  valueKey,
  value,
  previousValue,
  infoText,
  title,
  isCurrency,
}: Props) => {
  const isIterableGoalsStretchFlagOn = useSelector(selectIsIterableGoalsStretchFlagOn);
  let formattedValue = localize(value);

  if (percentageDisplayMetrics.includes(valueKey)) {
    formattedValue = formatAsPercent(value);
  } else if (isCurrency) {
    formattedValue = localizeCurrencyValue(value, { maximumFractionDigits: 0 });
  }

  return (
    <PerformanceMetricWrapper>
      <MetricTitle>
        <span>{title}</span>
        <HelpIcon tooltipText={infoText} />
      </MetricTitle>
      <MetricValue>{formattedValue}</MetricValue>
      {isIterableGoalsStretchFlagOn && (
        <PeriodReporting value={value} previousValue={previousValue} />
      )}
    </PerformanceMetricWrapper>
  );
};
